import React from 'react';
import { useParams } from 'react-router-dom';
import { CalendarIcon, ClockIcon, UserIcon, ArrowLeftIcon, ArrowRightIcon, CheckCircleIcon, BriefcaseIcon, TrendingUpIcon, SearchCheckIcon } from 'lucide-react';
import { SchoolListings } from '../components/SchoolListings';
import { useSchoolSearch } from '../hooks/useSchoolSearch';
import { useGeoLocation } from '../hooks/useGeoLocation';
import { useMetaTags } from '../hooks/useMetaTags';
import { articles, Article } from '../data/articlesData';
import { areaOfStudyData } from '../data/areaOfStudyData';
import { getDefaultProgram } from '../data/filterOptions';
interface ArticleDetailPageProps {
  onNavigate: (page: string, articleSlug?: string) => void;
  onProgramChange: (programId: number) => void;
}
// Map an article's area of study to the search params that make the embedded
// listings match the article topic. mProgram uses the area's "General"
// program (area * 100); area 0 (editorial/general) falls back to 900.
function getSearchTargets(areaStudyId: number): {
  degreeInterest: number;
  mProgram: number;
} {
  const mProgram = areaStudyId >= 1 ? getDefaultProgram(areaStudyId) : 900;
  // Trades skew toward Associate's/Certificate; everything else Bachelor's.
  const degreeInterest = areaStudyId === 7 ? 2 : 3;
  return {
    degreeInterest,
    mProgram
  };
}
export function ArticleDetailPage({
  onNavigate,
  onProgramChange
}: ArticleDetailPageProps) {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const article: Article | undefined = articles.find(a => a.slug === slug);
  const area = article ? areaOfStudyData[article.areaStudyId] : undefined;
  const {
    degreeInterest,
    mProgram
  } = getSearchTargets(article?.areaStudyId ?? 0);
  const {
    geo
  } = useGeoLocation();
  const {
    schools,
    loading,
    error
  } = useSchoolSearch({
    degreeInterest,
    mProgram,
    zipCode: geo.zipCode
  });
  useMetaTags({
    title: article ? `${article.title} | FindOnlineDegrees` : 'Article Not Found | FindOnlineDegrees',
    description: article?.excerpt || 'Read expert guides for online students.',
    keywords: article ? `${article.category}, online degree, ${area?.name || ''}, ${article.title}` : 'online education articles',
    ogType: 'article',
    author: article?.author || 'FindOnlineDegrees Editorial Team'
  });
  if (!article) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-id="element-849" data-mp="to2093y1id9q">
        <div className="text-center max-w-md" data-id="element-850" data-mp="tlpbo7xs14np">
          <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-851" data-mp="onxkjg4yhmkk">
            Article not found
          </h1>
          <p className="text-gray-600 mb-6" data-id="element-852" data-mp="13cjypeiuk8a7">
            We couldn't find the article you were looking for.
          </p>
          <button onClick={() => onNavigate('articles')} className="inline-flex items-center gap-2 bg-[#1a2332] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#2a374a] transition-colors" data-id="element-853" data-mp="yfjznnkf05sl">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-854" data-mp="1r0qkjgh3njdq" />
            Browse all articles
          </button>
        </div>
      </div>;
  }
  const topic = area?.name || article.category;
  const relatedArticles = articles.filter(a => a.areaStudyId === article.areaStudyId && a.slug !== article.slug).slice(0, 3);
  return <div className="min-h-screen bg-gray-50" data-id="element-855" data-mp="1my3mgxtu5283">
      {/* Breadcrumb / Back */}
      <div className="bg-white border-b border-gray-200" data-id="element-856" data-mp="189pmcunszrv8">
        <div className="max-w-4xl mx-auto px-4 py-4" data-id="element-857" data-mp="1d1h2scd8wh7n">
          <button onClick={() => onNavigate('articles')} className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#3b82f6] transition-colors" data-id="element-858" data-mp="1d28x7jv3w8wc">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-859" data-mp="13ch30qqx5hse" />
            Back to Articles
          </button>
        </div>
      </div>

      {/* Article Hero */}
      <article className="bg-white" data-id="element-860" data-mp="pe91e5ixtsaw">
        <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-861" data-mp="ub988inm2z32">
          <div className="inline-block bg-blue-50 text-[#3b82f6] px-4 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-862" data-mp="uctyvx6t1xvn">
            {article.category}
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-863" data-mp="1snc29kb3vkgt">
            {article.title}
          </h1>

          <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-864" data-mp="1ix8xynyjak36">
            {article.excerpt || `A complete guide to ${topic.toLowerCase()} for online students in 2026.`}
          </p>

          {article.heroImage && <img src={article.heroImage} alt={article.title} className="w-full rounded-2xl mb-8 object-cover" data-id="element-865" data-mp="18y1m657g6xi6" />}

          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 pb-8 border-b border-gray-200" data-id="element-866" data-mp="zywot27mha7l">
            <div className="flex items-center gap-2" data-id="element-867" data-mp="2za7glcprsr09">
              <UserIcon className="w-4 h-4" data-id="element-868" data-mp="2uc5zuyfth3ry" />
              <span data-id="element-869" data-mp="2udh4f0h36fk4">{article.author}</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-870" data-mp="31vyh1fk9eahb">
              <CalendarIcon className="w-4 h-4" data-id="element-871" data-mp="31v5giwbw8h7m" />
              <span data-id="element-872" data-mp="36ui2leuxxk95">{article.date}</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-873" data-mp="36sx79n0sxxa8">
              <ClockIcon className="w-4 h-4" data-id="element-874" data-mp="3bl8bh22ks58l" />
              <span data-id="element-875" data-mp="3bnl0u0anxhvo">{article.readTime} read</span>
            </div>
          </div>
        </div>
      </article>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 py-12" data-id="element-876" data-mp="2ixcedw93gbba">
        <div className="prose prose-lg max-w-none" data-id="element-877" data-mp="2iynm275nu37o">
          {/* Rich, hand-published body (when the article ships full content) */}
          {article.content && <div className="mb-12" data-id="element-878" data-mp="2stxp8y7sogn1">
              {article.content.map((block, i) => {
            if (block.type === 'h2') {
              return <h2 key={i} className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-879" data-mp="851h5m61vkch">
                      {block.text}
                    </h2>;
            }
            if (block.type === 'h3') {
              return <h3 key={i} className="text-xl font-bold text-gray-900 mt-8 mb-3" data-id="element-880" data-mp="248pefk5jcyk6">
                      {block.text}
                    </h3>;
            }
            if (block.type === 'ul') {
              return <ul key={i} className="list-disc pl-6 mb-4 text-gray-700 space-y-2" data-id="element-881" data-mp="1pjt75pbazpkm">
                      {block.items?.map((item, j) => <li key={j} data-id="element-882" data-mp="1za4ellu74gw9">{item}</li>)}
                    </ul>;
            }
            return <p key={i} className="text-gray-700 leading-relaxed mb-4" data-id="element-883" data-mp="16s0k0bix0ykt" data-mpi={i}>
                    {block.text}
                  </p>;
          })}
            </div>}

          {/* Auto-generated, area-based body (short articles without content) */}
          {!article.content && <>
              {/* Introduction */}
              <section className="mb-12" data-id="element-884" data-mp="3i9c3o1sdxzvi">
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-885" data-mp="34gvxis46tlqd">
                  {area?.description || `Choosing the right online program is one of the most important decisions you'll make for your career.`}{' '}
                  This guide breaks down what you need to know about{' '}
                  {topic.toLowerCase()} — from career outcomes and earning
                  potential to what makes a program worth your time and money.
                </p>
                <p className="text-gray-700 leading-relaxed" data-id="element-886" data-mp="3s55nm68fovnb">
                  We've pulled together the latest data and practical advice so
                  you can compare accredited online options with confidence and
                  find the path that fits your goals.
                </p>
              </section>

              {/* Career outlook */}
              {area && <section className="mb-12" data-id="element-887" data-mp="33f03pc3ftbof">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-888" data-mp="33e74qxapzqgu">
                    <TrendingUpIcon className="w-6 h-6 text-[#e8594f]" data-id="element-889" data-mp="2ygorwvtl0r3k" />
                    Career Outlook &amp; Salary
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4" data-id="element-890" data-mp="2tj6ua1l0lyav">
                    {area.careerOutlook}
                  </p>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6" data-id="element-891" data-mp="3m4ycxviz1atn">
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-892" data-mp="3hehx64rohd44">
                      Typical Salary Range
                    </p>
                    <p className="text-2xl font-bold text-gray-900" data-id="element-893" data-mp="3cdlshozi5mxn">
                      {area.salaryRange}
                    </p>
                  </div>
                </section>}

              {/* Common career paths */}
              {area?.jobTitles?.length ? <section className="mb-12" data-id="element-894" data-mp="h9f0ho7i5z9o">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-895" data-mp="l8xjgefrk09v">
                    <BriefcaseIcon className="w-6 h-6 text-[#e8594f]" data-id="element-896" data-mp="q8jxur7bvmk9" />
                    Common Career Paths
                  </h2>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 list-none pl-0" data-id="element-897" data-mp="v86c93yw78un">
                    {area.jobTitles.map(title => <li key={title} className="flex items-center gap-2.5 text-gray-700" data-id="element-898" data-mp="104n9cno2kxjv">
                        <CheckCircleIcon className="w-5 h-5 text-[#3b82f6] flex-shrink-0" data-id="element-899" data-mp="18zbg6i9amnas" />
                        <span data-id="element-900" data-mp="1dwjozs8jxezd">{title}</span>
                      </li>)}
                  </ul>
                </section> : null}

              {/* Why study */}
              {area?.whyStudy && <section className="mb-12" data-id="element-901" data-mp="1nty5fruh63ro">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-902" data-mp="t6bvamsr3v68">
                    Why Study {topic}?
                  </h2>
                  <p className="text-gray-700 leading-relaxed" data-id="element-903" data-mp="x7nm2vnd2mg0">
                    {area.whyStudy}
                  </p>
                </section>}
            </>}

          {/* Embedded School Listings */}
          <section className="mb-12" data-id="element-904" data-mp="16ys0nu94k5ab">
            <div className="bg-blue-50 border-l-4 border-[#3b82f6] p-6 rounded-r-lg mb-8" data-id="element-905" data-mp="1bwtoeshhwss4">
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-906" data-mp="1guby4l3c7c16">
                Compare Top Online {topic} Programs
              </h3>
              <p className="text-sm text-gray-600" data-id="element-907" data-mp="1kxhzpffi8f1b">
                Based on the criteria discussed in this article, here are
                accredited online programs worth considering:
              </p>
            </div>

            <SchoolListings schools={schools} loading={loading} error={error} selectedAreaId={article.areaStudyId || 9} selectedProgram={mProgram} onProgramChange={onProgramChange} hideSidebar={true} data-id="element-908" data-mp="3mqfz5u3se5v8" />
          </section>

          {/* What to look for + conclusion (auto-generated body only) */}
          {!article.content && <>
              {area?.whatToLookFor && <section className="mb-12" data-id="element-909" data-mp="35cwn9qcmjshq">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-910" data-mp="6w2g6wavugoq">
                    <SearchCheckIcon className="w-6 h-6 text-[#e8594f]" data-id="element-911" data-mp="1z2a4le19dxm" />
                    What to Look for in a Program
                  </h2>
                  <p className="text-gray-700 leading-relaxed" data-id="element-912" data-mp="3t66e17evsk1t">
                    {area.whatToLookFor}
                  </p>
                </section>}

              <section className="mb-12" data-id="element-913" data-mp="2hqq3y7xr2qc7">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-914" data-mp="2dnl3sc1570qq">
                  The Bottom Line
                </h2>
                <p className="text-gray-700 leading-relaxed" data-id="element-915" data-mp="2f4h08oi6lsdq">
                  The right online program is the one that aligns with your
                  career goals, budget, and schedule. Use the comparison above
                  to explore accredited options and request information from the
                  schools that fit your needs.
                </p>
              </section>
            </>}
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && <section className="border-t border-gray-200 pt-10" data-id="element-916" data-mp="2157j9mm5iu8p">
            <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-917" data-mp="1mg31g6j9qs14">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5" data-id="element-918" data-mp="1rewfc02gwl1t">
              {relatedArticles.map(rel => <a key={rel.id} href={`/articles/${rel.slug}`} onClick={e => {
            e.preventDefault();
            onNavigate('articles', rel.slug);
          }} className="group bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow flex flex-col" data-id="element-919" data-mp="1df4txe9mg2ds">
                  <span className="text-[11px] font-semibold text-[#3b82f6] uppercase tracking-wide mb-2" data-id="element-920" data-mp="17tmgs24p9u8k">
                    {rel.category}
                  </span>
                  <h3 className="text-sm font-bold text-gray-900 leading-snug mb-3 line-clamp-2 group-hover:text-[#3b82f6] transition-colors" data-id="element-921" data-mp="1hrk4moindp0n">
                    {rel.title}
                  </h3>
                  <span className="mt-auto text-xs font-semibold text-[#e8594f] flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-922" data-mp="1cqmki6e2ukwi">
                    Read <ArrowRightIcon className="w-3 h-3" data-id="element-923" data-mp="1mjbn0brngofp" />
                  </span>
                </a>)}
            </div>
          </section>}
      </div>
    </div>;
}